import { useKindeAuth } from '@kinde-oss/kinde-auth-react'
import { ArrowLeft } from 'lucide-react'
import { useEffect, useState } from 'react'
import { useParams } from 'react-router'

import { useCard } from '@/hooks/useCard'
import { useUserContext } from '@/providers/user/useUserContext'
import { Button } from '@/ui-components/Button'
import Loading from '@/ui-components/Loading'
import { CardSpend } from '@/views/Cards/CardSpend'
import { CardControls } from '@/views/ViewCardDetails/CardControls'
import MainCardDetails from '@/views/ViewCardDetails/MainCardDetails'

export const ViewCardDetailsPage = () => {
  const { cardId } = useParams()
  const { getAccessToken } = useKindeAuth()
  const { activeOrganizationId, activeConnectedAccountId } = useUserContext()

  const [isLoading, setIsLoading] = useState(true)
  const { setCard, getCard, hasCard } = useCard()

  useEffect(() => {
    if (!cardId || !activeOrganizationId || !activeConnectedAccountId) {
      setIsLoading(false)
      return
    }

    // If the card in the store matches the cardId, use it and skip fetching
    if (hasCard(cardId)) {
      setIsLoading(false)
      return
    }

    const fetchCardData = async () => {
      try {
        const response = await fetch(
          `/api/pay/org/${activeOrganizationId}/connected-account/${activeConnectedAccountId}/card/${cardId}`,
          {
            headers: {
              Authorization: `Bearer ${await getAccessToken()}`,
            },
          },
        )

        if (!response.ok) {
          throw new Error('Failed to fetch card data')
        }

        const data = await response.json()
        setCard(data)
      } catch (error) {
        console.error('Error fetching card data:', error)
      } finally {
        setIsLoading(false)
      }
    }

    fetchCardData()
  }, [cardId, activeOrganizationId, activeConnectedAccountId, getAccessToken, setCard, hasCard])

  if (isLoading) {
    return <Loading />
  }

  if (!activeOrganizationId || !activeConnectedAccountId) {
    return <p className="p-6 text-center">Please select an organization and connected account to view card details.</p>
  }

  // Get the card from store using cardId
  const cardDetails = cardId ? getCard(cardId) : null

  // No cardId and loading is finished
  if (!cardId || !cardDetails) {
    return <div>No card found</div>
  }

  return (
    <>
      <Button
        plain
        className="mb-2"
        to="/cards"
        aria-label="Go back"
      >
        <ArrowLeft data-slot="icon" />
        Back
      </Button>

      <div
        className="w-full flex flex-col xl:flex-row gap-8"
        data-testid="view-card-details-page"
      >
        <MainCardDetails
          cardDetails={cardDetails}
          onCardUpdate={setCard}
        />
        <div className="w-full flex flex-col gap-8">
          <CardControls
            currency={cardDetails.currencyLimit}
            limits={cardDetails.limits}
          />
          <CardSpend cardId={cardId} />
        </div>
      </div>
    </>
  )
}

export default ViewCardDetailsPage
